import useComponentSize from '@rehooks/component-size';
import React, { useRef, useState } from 'react';
import styled, { css, FlattenSimpleInterpolation } from 'styled-components';
import { Box, BoxStyle } from '~common/components/box.component';
import { Span } from '~common/components/span.component';
import { time, transitions } from '~web/view/theme/utils';

export enum Side {
  Left = 0,
  Right = 1,
}

interface Props {
  optionLeft: string;
  optionRight: string;
  initialOption?: Side;
  onChange?: (option: Side) => void;
}

export const OptionsSwitch: React.FunctionComponent<Props> = ({
  optionLeft,
  optionRight,
  initialOption,
  onChange,
  ...rest
}) => {
  const [selectedOption, setSelectedOption] = useState<Side>(
    initialOption ? initialOption : Side.Left,
  );

  const leftOptionRef = useRef<HTMLDivElement>(undefined);
  const rightOptionRef = useRef<HTMLDivElement>(undefined);
  const leftOptionWidth = useComponentSize(leftOptionRef).width;
  const rightOptionWidth = useComponentSize(rightOptionRef).width;

  const switchValue = (): void => {
    const newSide = selectedOption === Side.Left ? Side.Right : Side.Left;
    setSelectedOption(newSide);
    setTimeout((): void => {
      onChange?.(newSide);
    }, time.transitions.default);
  };

  return (
    <Container {...rest} onClick={switchValue}>
      <Toggle
        leftWidth={leftOptionWidth}
        rightWidth={rightOptionWidth}
        side={selectedOption}
        initialOption={initialOption}
        className={'options-switch-toggle'}
      />
      <BoxWithRef ref={leftOptionRef}>
        <Label className={'options-switch-label'} selected={selectedOption === Side.Left}>
          {optionLeft}
        </Label>
      </BoxWithRef>
      <BoxWithRef ref={rightOptionRef}>
        <Label className={'options-switch-label'} selected={selectedOption === Side.Right}>
          {optionRight}
        </Label>
      </BoxWithRef>
    </Container>
  );
};

const Container = styled(Box)`
  box-sizing: border-box;
  background: ${(props): string => props.theme.colors.gray.v3};
  border-radius: 18px;
  width: fit-content;
  cursor: pointer;
  user-select: none;
  margin-right: auto;
`;

const BoxWithRef = styled.div`
  ${BoxStyle};
`;

const Label = styled(({ selected, ...rest }) => <Span {...rest} />)<{ selected?: boolean }>`
  font-weight: bold;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: 0.5px;
  padding: 6px 12px;
  z-index: ${(props): number => props.theme.zIndex.over};
  min-width: 32px;
  color: ${(props): string =>
    props.selected ? props.theme.colors.black : props.theme.colors.gray.v7};
  transition: color ${transitions.default};
`;

interface StylingProps {
  leftWidth: number;
  rightWidth: number;
  side: Side;
  initialOption: Side;
}

const toggleTransform = (props: StylingProps): FlattenSimpleInterpolation | null => {
  if (props.initialOption === Side.Left && props.side === Side.Right) {
    return css`
      transform: translateX(${props.leftWidth}px);
    `;
  }
  if (props.initialOption === Side.Right && props.side === Side.Left) {
    return css`
      transform: translateX(-${props.rightWidth}px);
    `;
  }
  return null;
};

const toggleHorizontalPositioning = (props: StylingProps): FlattenSimpleInterpolation | null => {
  if (props.initialOption === Side.Left) {
    return css`
      left: 0;
    `;
  } else {
    return css`
      right: 0;
    `;
  }
};

const toggleWidth = (props: StylingProps): string =>
  props.side === Side.Left ? `${props.leftWidth}px` : `${props.rightWidth}px`;

const Toggle = styled(({ leftWidth, rightWidth, side, initialOption, ...rest }) => (
  <Box {...rest} />
))<StylingProps>`
  position: absolute;
  top: 0;
  bottom: 0;

  background: ${(props): string => props.theme.colors.white};
  border-radius: 18px;
  width: ${(props): string => toggleWidth(props)};
  transition: transform ${transitions.default};

  ${(props): FlattenSimpleInterpolation | null => toggleHorizontalPositioning(props)};
  ${(props): FlattenSimpleInterpolation | null => toggleTransform(props)};
`;



// WEBPACK FOOTER //
// /codebuild/output/src426716145/src/modules/web/src/js/view/components/ui/options-switch.component.tsx